import React, { useState, useEffect } from "react";
import "./createAgentDialog.css";
import { User, Building, Phone, Mail, Hash, CreditCard, Briefcase } from "lucide-react";

const BUSINESS_TYPES   = ["Proprietor", "Partnership", "Pvt Ltd", "LLP", "Other"];
const MEMBERSHIP_TYPES = ["IATA", "TAFI", "TAAI", "CLIA", "TRUE", "Other"];
const ACCOUNT_TYPES    = ["Savings", "Current", "Other"];

const emptyAddress = {
  addressLine1: "", addressLine2: "",
  city: "", stateProvince: "", pinCode: "", country: "India",
};

const emptyForm = {
  agentType:  "Individual",
  tradeName:  "",
  contactPersonName: "",
  designation: "",
  contactNumber: "",
  alternateContactNumber: "",
  whatsappNumber: "",
  email: "",
  address: { ...emptyAddress },
  businessType: "",
  panNumber: "",
  gstNumber: "",
  travelAgencyRegistrationNumber: "",
  membershipType: "",
  membershipNumber: "",
  bankName: "",
  branchName: "",
  beneficiaryName: "",
  accountNumber: "",
  accountType: "",
  ifscSwiftIban: "",
  isActive: true,
};

/* ── IconInput defined OUTSIDE the main component so React never treats it
      as a new component type on re-render, which would steal focus ────────── */
function IconInput({ icon: Icon, ...props }) {
  return (
    <div className="agent-input-icon">
      <Icon size={14} />
      <input {...props} />
    </div>
  );
}

export default function CreateAgentDialog({
  open, onClose, onSubmit, isLoading = false, initialData = null, isEdit = false,
}) {
  const [form, setForm]     = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        ...emptyForm,
        ...initialData,
        address: { ...emptyAddress, ...(initialData.address || {}) },
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setSaving(false);
  }, [open, initialData]);

  if (!open) return null;

  /* ── Handlers ─────────────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("address.")) {
      const key = name.split(".")[1];
      setForm((f) => ({ ...f, address: { ...f.address, [key]: value } }));
    } else {
      setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!form.contactPersonName.trim()) err.contactPersonName = "Contact person name is required";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Invalid email format";
    if (form.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.panNumber)) err.panNumber = "Invalid PAN (e.g. ABCDE1234F)";
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = JSON.parse(JSON.stringify(form));
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    if (payload.address) {
      Object.keys(payload.address).forEach((k) => { if (payload.address[k] === "") delete payload.address[k]; });
      if (!Object.keys(payload.address).length) delete payload.address;
    }

    setSaving(true);
    try { await onSubmit(payload); }
    catch (_) { /* parent handles alert */ }
    finally { setSaving(false); }
  };

  const busy = saving || isLoading;

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <div className="agent-dialog-overlay">
      <div className="agent-dialog-container">

        {/* Sticky header */}
        <div className="agent-dialog-header">
          <h2>{isEdit ? "Edit Agent" : "Add New Agent"}</h2>
          <button className="agent-close-btn" onClick={onClose} disabled={busy}>×</button>
        </div>

        {/* Scrollable body */}
        <div className="agent-dialog-body">

          <div className="agent-id-badge">
            🪪 {isEdit && initialData?.agentId
              ? `Agent ID: ${initialData.agentId}`
              : "Agent ID will be auto-generated on save"}
          </div>

          <form onSubmit={handleSubmit}>

            {/* ── 1. Basic Details ── */}
            <div className="agent-form-section">
              <h3><User size={14} /> Basic Details</h3>
              <div className="agent-form-grid">

                <div className="agent-full-width">
                  <label>Agent Type</label>
                  <div className="agent-type-pills">
                    {["Individual", "Firm"].map((t) => (
                      <button
                        key={t} type="button" disabled={busy}
                        className={`agent-type-pill${form.agentType === t ? " selected" : ""}`}
                        onClick={() => setForm((f) => ({ ...f, agentType: t }))}
                      >
                        {t === "Individual" ? "👤 Individual" : "🏢 Firm"}
                      </button>
                    ))}
                  </div>
                </div>

                {form.agentType === "Firm" && (
                  <div className="agent-full-width">
                    <label>Trade Name / Firm Name</label>
                    <input
                      type="text" name="tradeName"
                      value={form.tradeName} onChange={handleChange}
                      placeholder="Trade or DBA name" disabled={busy}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ── 2. Contact Details ── */}
            <div className="agent-form-section">
              <h3><Phone size={14} /> Contact Details</h3>
              <div className="agent-form-grid">

                <div className={errors.contactPersonName ? "agent-field-error" : ""}>
                  <label>Contact Person Name *</label>
                  <input
                    type="text" name="contactPersonName"
                    value={form.contactPersonName} onChange={handleChange}
                    placeholder="Full name" disabled={busy}
                  />
                  {errors.contactPersonName && <span className="agent-error-text">{errors.contactPersonName}</span>}
                </div>

                <div>
                  <label>Designation</label>
                  <input
                    type="text" name="designation"
                    value={form.designation} onChange={handleChange}
                    placeholder="e.g. Manager, Director" disabled={busy}
                  />
                </div>

                <div>
                  <label>Contact Number</label>
                  <IconInput
                    icon={Phone} type="tel" name="contactNumber"
                    value={form.contactNumber} onChange={handleChange}
                    placeholder="+91 9876543210" disabled={busy}
                  />
                </div>

                <div>
                  <label>Alternate Contact Number</label>
                  <IconInput
                    icon={Phone} type="tel" name="alternateContactNumber"
                    value={form.alternateContactNumber} onChange={handleChange}
                    placeholder="+91 9876543210" disabled={busy}
                  />
                </div>

                <div>
                  <label>WhatsApp Number <span style={{ fontWeight: 400, color: "#9ca3af" }}>(if different)</span></label>
                  <IconInput
                    icon={Phone} type="tel" name="whatsappNumber"
                    value={form.whatsappNumber} onChange={handleChange}
                    placeholder="+91 9876543210" disabled={busy}
                  />
                </div>

                <div className={errors.email ? "agent-field-error" : ""}>
                  <label>Email ID</label>
                  <IconInput
                    icon={Mail} type="email" name="email"
                    value={form.email} onChange={handleChange}
                    placeholder="agent@example.com" disabled={busy}
                  />
                  {errors.email && <span className="agent-error-text">{errors.email}</span>}
                </div>

                <div>
                  <label>Address Line 1</label>
                  <input
                    type="text" name="address.addressLine1"
                    value={form.address.addressLine1} onChange={handleChange}
                    placeholder="Street / Building" disabled={busy}
                  />
                </div>

                <div>
                  <label>Address Line 2</label>
                  <input
                    type="text" name="address.addressLine2"
                    value={form.address.addressLine2} onChange={handleChange}
                    placeholder="Area / Landmark" disabled={busy}
                  />
                </div>

                <div>
                  <label>City</label>
                  <input
                    type="text" name="address.city"
                    value={form.address.city} onChange={handleChange}
                    placeholder="City" disabled={busy}
                  />
                </div>

                <div>
                  <label>State / Province</label>
                  <input
                    type="text" name="address.stateProvince"
                    value={form.address.stateProvince} onChange={handleChange}
                    placeholder="State" disabled={busy}
                  />
                </div>

                <div>
                  <label>Pin Code</label>
                  <input
                    type="text" name="address.pinCode"
                    value={form.address.pinCode} onChange={handleChange}
                    placeholder="Pin / Postal code" disabled={busy}
                  />
                </div>

                <div>
                  <label>Country</label>
                  <input
                    type="text" name="address.country"
                    value={form.address.country} onChange={handleChange}
                    placeholder="Country" disabled={busy}
                  />
                </div>
              </div>
            </div>

            {/* ── 3. Business / Registration ── */}
            <div className="agent-form-section">
              <h3><Briefcase size={14} /> Business / Registration Details</h3>
              <div className="agent-form-grid">

                <div>
                  <label>Business Type</label>
                  <select name="businessType" value={form.businessType} onChange={handleChange} disabled={busy}>
                    <option value="">— Select —</option>
                    {BUSINESS_TYPES.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>

                <div className={errors.panNumber ? "agent-field-error" : ""}>
                  <label>PAN Number</label>
                  <input
                    type="text" name="panNumber"
                    value={form.panNumber} onChange={handleChange}
                    placeholder="ABCDE1234F" maxLength={10}
                    style={{ textTransform: "uppercase" }} disabled={busy}
                  />
                  {errors.panNumber && <span className="agent-error-text">{errors.panNumber}</span>}
                </div>

                <div>
                  <label>GST Number</label>
                  <input
                    type="text" name="gstNumber"
                    value={form.gstNumber} onChange={handleChange}
                    placeholder="22ABCDE1234F1Z5" maxLength={15}
                    style={{ textTransform: "uppercase" }} disabled={busy}
                  />
                </div>

                <div>
                  <label>Travel Agency Registration No.</label>
                  <IconInput
                    icon={Hash} type="text" name="travelAgencyRegistrationNumber"
                    value={form.travelAgencyRegistrationNumber} onChange={handleChange}
                    placeholder="Agency reg. number" disabled={busy}
                  />
                </div>

                <div>
                  <label>Membership Type</label>
                  <select name="membershipType" value={form.membershipType} onChange={handleChange} disabled={busy}>
                    <option value="">— Select —</option>
                    {MEMBERSHIP_TYPES.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>

                <div>
                  <label>Membership Number</label>
                  <IconInput
                    icon={Hash} type="text" name="membershipNumber"
                    value={form.membershipNumber} onChange={handleChange}
                    placeholder="IATA / TAFI / TAAI no." disabled={busy}
                  />
                </div>
              </div>
            </div>

            {/* ── 4. Payment Information ── */}
            <div className="agent-form-section">
              <h3><CreditCard size={14} /> Payment Information</h3>
              <div className="agent-form-grid">

                <div>
                  <label>Bank Name</label>
                  <IconInput
                    icon={Building} type="text" name="bankName"
                    value={form.bankName} onChange={handleChange}
                    placeholder="e.g. HDFC Bank" disabled={busy}
                  />
                </div>

                <div>
                  <label>Branch Name</label>
                  <input
                    type="text" name="branchName"
                    value={form.branchName} onChange={handleChange}
                    placeholder="Branch name" disabled={busy}
                  />
                </div>

                <div>
                  <label>Beneficiary Name</label>
                  <input
                    type="text" name="beneficiaryName"
                    value={form.beneficiaryName} onChange={handleChange}
                    placeholder="Name as per bank account" disabled={busy}
                  />
                </div>

                <div>
                  <label>Account Number</label>
                  <input
                    type="text" name="accountNumber"
                    value={form.accountNumber} onChange={handleChange}
                    placeholder="Bank account number" disabled={busy}
                  />
                </div>

                <div>
                  <label>Account Type</label>
                  <select name="accountType" value={form.accountType} onChange={handleChange} disabled={busy}>
                    <option value="">— Select —</option>
                    {ACCOUNT_TYPES.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </div>

                <div>
                  <label>IFSC / SWIFT / IBAN</label>
                  <input
                    type="text" name="ifscSwiftIban"
                    value={form.ifscSwiftIban} onChange={handleChange}
                    placeholder="e.g. HDFC0001234" maxLength={34}
                    style={{ textTransform: "uppercase" }} disabled={busy}
                  />
                </div>
              </div>
            </div>

            {/* ── 5. Status ── */}
            <div className="agent-form-section">
              <h3>Status</h3>
              <div className="agent-status-toggle">
                <label className="agent-toggle-label">
                  <input
                    type="checkbox" name="isActive"
                    checked={form.isActive} onChange={handleChange}
                    disabled={busy}
                  />
                  <span className={`agent-toggle-slider${form.isActive ? " active" : ""}`} />
                  <span className="agent-toggle-text">{form.isActive ? "Active" : "Inactive"}</span>
                </label>
                <small style={{ color: "#6b7280", fontSize: 12 }}>
                  Inactive agents won't appear in assignment lists
                </small>
              </div>
            </div>

            {/* ── Actions ── */}
            <div className="agent-dialog-actions">
              <button type="button" className="agent-btn-outline" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="agent-btn-primary" disabled={busy}>
                {busy ? "Saving..." : isEdit ? "Update Agent" : "Create Agent"}
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
}